<script setup lang="ts">
/*
 * Bottom tab bar below `md`, taking the platform dock's place on phones:
 * Home, Browse, Search (opens the overlay), Wishlist (count badge) and
 * Account. The active tab lights its icon amber and shows a 3px amber bar above it.
 * Height is `--bottom-tabs-h` (app.css) so `<main>` and fixed bars (product
 * buy bar, back-to-top) can clear it.
 */
import { computed, type Component } from 'vue';
import { RouterLink, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { Heart, House, LayoutGrid, Search, User } from 'lucide-vue-next';
import CountBadge from './CountBadge.vue';
import { useAuthStore } from '../../stores/auth';
import { useFavoritesStore } from '../../stores/favorites';
import { useUiStore } from '../../stores/ui';

interface Tab { key: string; label: string; icon: Component; to?: string; active: boolean; count?: number }

const { t } = useI18n();
const route = useRoute();
const auth = useAuthStore();
const favorites = useFavoritesStore();
const ui = useUiStore();

const ACCOUNT_PREFIXES = ['/account', '/orders', '/login', '/register'];

const tabs = computed<Tab[]>(() => [
  { key: 'home', label: t('header.tabs.home'), icon: House, to: '/', active: route.path === '/' },
  { key: 'browse', label: t('header.tabs.browse'), icon: LayoutGrid, to: '/catalog', active: route.name === 'catalog' },
  { key: 'search', label: t('header.tabs.search'), icon: Search, active: ui.paletteOpen },
  { key: 'wishlist', label: t('header.tabs.wishlist'), icon: Heart, to: '/favorites', active: route.path.startsWith('/favorites'), count: favorites.count },
  {
    key: 'account',
    label: t('header.tabs.account'),
    icon: User,
    to: auth.isAuthenticated ? '/account' : '/login',
    active: ACCOUNT_PREFIXES.some((p) => route.path.startsWith(p)),
  },
]);

const TAB = 'relative flex h-full flex-col items-center justify-center gap-1 text-[10px] font-semibold transition-colors';
</script>

<template>
  <nav
    :aria-label="t('header.tabs.label')"
    class="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-bg-base pb-[env(safe-area-inset-bottom)] md:hidden"
  >
    <ul class="grid h-16 grid-cols-5">
      <li v-for="tab in tabs" :key="tab.key">
        <component
          :is="tab.to ? RouterLink : 'button'"
          :to="tab.to"
          :type="tab.to ? undefined : 'button'"
          :aria-current="tab.active && tab.to ? 'page' : undefined"
          :aria-label="tab.label"
          :class="[TAB, 'w-full', tab.active ? 'text-accent' : 'text-text-dim active:text-text-base']"
          @click="tab.key === 'search' ? ui.openPalette() : undefined"
        >
          <span v-if="tab.active" class="absolute inset-x-5 top-0 h-[3px] rounded-b-full bg-accent" aria-hidden="true"></span>
          <span class="relative">
            <component :is="tab.icon" class="size-6" aria-hidden="true" />
            <CountBadge v-if="tab.count !== undefined" :count="tab.count" />
          </span>
          {{ tab.label }}
        </component>
      </li>
    </ul>
  </nav>
</template>
